import { Typography, Divider } from '@unity/cloud-ui';

import z from 'zod';
import { AccordionGroup, Accordion, accordionGroupSchema, accordionSchema } from './accordion';
import { Important, Note, Tip, Warning } from './admonitions';
import { Card, CardGrid, cardGridSchema, cardSchema } from './card';
import { Code, CodeGroup, codeGroupSchema } from './code';
import { Expandable, expandableSchema } from './expandable';
import { Field, FieldGroup, fieldGroupSchema, fieldSchema } from './field';
import { Frame, frameSchema } from './frame';
import { GlossaryTerm, glossaryTermSchema } from './glossary-term';
import { Header } from './header';
import { Icon, iconSchema } from './icon';
import { Image } from './image';
import { Link } from './link';
import { List } from './list';
import { ListItem, listItemSchema } from './list-item';
import { ListGroup, listGroupSchema } from './list-group';
import { Mermaid } from './mermaid';
import { PreformattedText } from './preformatted-text';
import { Step, Steps, stepSchema, stepsSchema } from './steps';
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  tableSchema,
  tableCellSchema,
  tableHeaderCellSchema,
  tableRowSchema,
  tableHeaderSchema,
  tableBodySchema,
} from './table';
import { Tabs, Tab, tabSchema, tabsSchema } from './tabs';
import { VideoEmbed, videoEmbedSchema } from './video-embed';
import { admonitionSchema } from './admonitions/admonition';
import { ScrollPoint, scrollPointSchema } from './scroll-point';
import { includeSchema } from './include/include';

/*
 * Custom components for MDX rendering.
 * Each component must be registered with its schema for validation in the content repo.
 * See: customComponentSchemas (below) for the mapping of components to their Zod schemas.
 */
const customComponents = {
  AccordionGroup,
  Accordion,
  Card,
  CardGrid,
  CardGroup: CardGrid,
  Caution: Warning,
  CodeGroup,
  Expandable,
  Field,
  FieldGroup,
  Frame,
  GlossaryTerm,
  Icon,
  Important,
  ListItem,
  ListGroup,
  Note,
  ScrollPoint,
  Step,
  Steps,
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  table: Table,
  thead: TableHeader,
  th: TableHeaderCell,
  tbody: TableBody,
  tr: TableRow,
  td: TableCell,
  Tabs,
  Tab,
  Tip,
  VideoEmbed,
  Warning,
};

/*
 * Zod schemas for custom components.
 * Each schema is used for validation in the content repo.
 * The "Include" component is handled separately as it does not have nor require a custom component implementation.
 */
export const customComponentSchemas: Record<keyof typeof customComponents | 'Include', z.ZodTypeAny> = {
  AccordionGroup: accordionGroupSchema,
  Accordion: accordionSchema,
  Card: cardSchema,
  CardGrid: cardGridSchema,
  CardGroup: cardGridSchema,
  Caution: admonitionSchema,
  CodeGroup: codeGroupSchema,
  Expandable: expandableSchema,
  Field: fieldSchema,
  FieldGroup: fieldGroupSchema,
  Frame: frameSchema,
  GlossaryTerm: glossaryTermSchema,
  Icon: iconSchema,
  Important: admonitionSchema,
  Include: includeSchema,
  ListItem: listItemSchema,
  ListGroup: listGroupSchema,
  Note: admonitionSchema,
  ScrollPoint: scrollPointSchema,
  Step: stepSchema,
  Steps: stepsSchema,
  Table: tableSchema,
  TableHeader: tableHeaderSchema,
  TableHeaderCell: tableHeaderCellSchema,
  TableBody: tableBodySchema,
  TableRow: tableRowSchema,
  TableCell: tableCellSchema,
  table: tableSchema,
  thead: tableHeaderSchema,
  th: tableHeaderCellSchema,
  tbody: tableBodySchema,
  tr: tableRowSchema,
  td: tableCellSchema,
  Tabs: tabsSchema,
  Tab: tabSchema,
  Tip: admonitionSchema,
  VideoEmbed: videoEmbedSchema,
  Warning: admonitionSchema,
};

const components = {
  a: Link,
  h1: (props) => <Header {...props} variant="h1" />,
  h2: (props) => <Header {...props} variant="h2" />,
  h3: (props) => <Header {...props} variant="h3" />,
  h4: (props) => <Header {...props} variant="h4" />,
  h5: (props) => <Header {...props} variant="h5" />,
  h6: (props) => <Header {...props} variant="h6" />,
  code: Code,
  pre: PreformattedText,
  p: (props) => <Typography {...props} variant="body1" component="span" />,
  ul: (props) => <List {...props} component="ul" />,
  ol: (props) => <List {...props} component="ol" />,
  li: ListItem,
  hr: Divider,
  img: (props) => <Image {...props} />,
  Mermaid,
  // All custom components must be added to the customComponents map.
  ...customComponents,
};

export default components;
